/**
 * Pixel Office Renderer
 * Based on pixel-agents renderer approach - uses WeakMap sprite cache
 */

import type { Character, SpriteData, Desk, LayoutData, FloorColor } from "../types/index.js";
import {
  CHARACTER_SITTING_OFFSET_PX,
  CHARACTER_COUCH_SITTING_OFFSET_PX,
  CHARACTER_Z_SORT_OFFSET,
  OUTLINE_Z_SORT_OFFSET,
  SELECTED_OUTLINE_ALPHA,
  HOVERED_OUTLINE_ALPHA,
  BUBBLE_SITTING_OFFSET_PX,
  BUBBLE_VERTICAL_OFFSET_PX,
  TILE_SIZE as CONST_TILE_SIZE,
  INTERACTIVE_SUPPLIES,
  INTERACTIVE_GLOW_PULSE_SPEED,
  INTERACTIVE_GLOW_MIN_ALPHA,
  INTERACTIVE_GLOW_MAX_ALPHA,
  INTERACTIVE_HOVER_SCALE,
} from "../constants.js";
import type { IntegrationType, InteractiveObject } from "../types/index.js";
import { getFurniturePlacements, getAssetInfo, getCachedImage } from "../furniture/loader.js";
import { getDefaultLayout, layoutToTileMap, getTileColor } from "../layout.js";
import { getCachedSprite, getOutlineSprite } from "../sprites/cache.js";
import {
  getCharacterSprites,
  BUBBLE_PERMISSION_SPRITE,
  BUBBLE_WAITING_SPRITE,
  BUBBLE_SLEEP_SPRITE,
  BUBBLE_MESSAGE_SPRITE,
  BUBBLE_WARNING_SPRITE,
  BUBBLE_SETUP_SPRITE,
  BUBBLE_SPEECH_SPRITE,
  BUBBLE_DISCUSS_SPRITE,
  BUBBLE_SUCCESS_SPRITE,
  getInteractiveSupplySprite,
} from "../sprites/data.js";
import {
  hasFloorSprites,
  getColorizedFloorSprite,
  WALL_COLOR,
  hasWallSprites,
  getWallInstances,
  wallColorToHex,
} from "../tiles/index.js";
import { CharacterState, TILE_SIZE, TileType } from "../types/index.js";
import { getCharacterSprite } from "./character.js";
import { renderMatrixEffect } from "./matrixEffect.js";

// Collaboration visualization constants
const COLLAB_LINE_COLOR = "rgba(255, 208, 0, 0.4)"; // Rax yellow, semi-transparent
const COLLAB_LINE_GLOW_COLOR = "rgba(255, 208, 0, 0.15)"; // Glow color
const COLLAB_LINE_DASH = [4, 4]; // Dotted line pattern
const COLLAB_LINE_WIDTH = 2;
const COLLAB_GLOW_WIDTH = 8;
const MEETING_LINE_COLOR = "rgba(100, 180, 255, 0.2)"; // Subtle blue for meetings
const MEETING_LINE_WIDTH = 1;
const MEETING_LINE_DASH = [2, 6]; // More sparse dots for meetings

// Tool name to user-friendly status text (business-friendly, no tech jargon)
const TOOL_STATUS_MAP: Record<string, string> = {
  // Research & reading
  Read: "Reviewing...",
  Write: "Drafting...",
  Edit: "Revising...",
  Grep: "Searching...",
  Glob: "Looking up...",
  WebFetch: "Researching...",
  WebSearch: "Researching...",
  // Execution
  Bash: "Working...",
  Task: "Thinking...",
  // Communication
  SendMessage: "Messaging...",
  Email: "Emailing...",
  Slack: "Messaging...",
  Discord: "Messaging...",
  Telegram: "Messaging...",
  iMessage: "Texting...",
  WhatsApp: "Messaging...",
  // Organization
  Calendar: "Scheduling...",
  Schedule: "Scheduling...",
  CreateEvent: "Booking...",
  TodoWrite: "Planning...",
  NotebookEdit: "Taking notes...",
};

function getToolStatusText(tool: string): string {
  return TOOL_STATUS_MAP[tool] || "Working...";
}

// Working together visualization (multiple agents on same task)
const WORKING_TOGETHER_LINE_COLOR = "rgba(100, 255, 150, 0.35)"; // Green for teamwork
const WORKING_TOGETHER_GLOW_COLOR = "rgba(100, 255, 150, 0.15)"; // Glow color
const WORKING_TOGETHER_LINE_WIDTH = 2;
const WORKING_TOGETHER_GLOW_WIDTH = 6;

// Handoff animation visualization
const HANDOFF_ORB_COLOR = "rgba(255, 208, 0, 0.9)"; // Rax yellow orb
const HANDOFF_TRAIL_COLOR = "rgba(255, 208, 0, 0.3)"; // Trail behind orb
const HANDOFF_ORB_SIZE = 4; // Pixels

// User avatar focus line (when user focuses on an agent)
const USER_FOCUS_LINE_COLOR = "rgba(255, 215, 0, 0.25)"; // Golden focus line
const USER_FOCUS_LINE_WIDTH = 1;
const USER_FOCUS_DASH = [3, 3];

interface ZDrawable {
  zY: number;
  draw: (ctx: CanvasRenderingContext2D) => void;
}

// Cache the layout and tile map
let cachedLayoutData: LayoutData | null = null;
let cachedTileMap: number[][] | null = null;

function getLayout(): LayoutData {
  if (!cachedLayoutData) {
    cachedLayoutData = getDefaultLayout();
    cachedTileMap = layoutToTileMap(cachedLayoutData);
  }
  return cachedLayoutData;
}

function getTileMap(): number[][] {
  if (!cachedTileMap) {
    getLayout();
  }
  return cachedTileMap || [];
}

export function renderFrame(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  cols: number,
  rows: number,
  desks: Desk[],
  characters: Character[],
  zoom: number,
  panX: number,
  panY: number,
  interactiveObjects?: InteractiveObject[],
  hoveredObjectId?: string | null,
): void {
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, canvasWidth, canvasHeight);

  const layout = getLayout();
  const tileMap = getTileMap();

  const layoutCols = layout.cols;
  const layoutRows = layout.rows;

  const mapW = layoutCols * TILE_SIZE * zoom;
  const mapH = layoutRows * TILE_SIZE * zoom;
  // Add vertical padding to prevent character sprites from being cut off at top
  // Characters are 24px tall (16x24 sprite) and anchored at bottom, so they extend 24px above their position
  const characterHeightPadding = 24 * zoom;
  const offsetX = Math.floor((canvasWidth - mapW) / 2) + Math.round(panX);
  const offsetY = Math.floor((canvasHeight - mapH) / 2) + Math.round(panY) + characterHeightPadding / 2;

  const tileW = TILE_SIZE * zoom;
  const tileH = TILE_SIZE * zoom;

  // ===== FLOOR TILES =====
  const useSpriteFloors = hasFloorSprites();
  for (let r = 0; r < layoutRows; r++) {
    for (let c = 0; c < layoutCols; c++) {
      const tileType = tileMap[r]?.[c] ?? TileType.VOID;
      if (tileType === TileType.VOID) continue;

      const tileColorIdx = r * layoutCols + c;
      const tileColor = layout.tileColors?.[tileColorIdx];

      // Wall tiles: solid color base (3D wall sprites are z-sorted later)
      if (tileType === TileType.WALL) {
        ctx.fillStyle = tileColor ? wallColorToHex(tileColor) : WALL_COLOR;
        ctx.fillRect(offsetX + c * tileW, offsetY + r * tileH, tileW, tileH);
        continue;
      }

      // Floor tiles: use sprite system if available
      if (useSpriteFloors && tileColor) {
        const floorSprite = getColorizedFloorSprite(tileType, tileColor);
        const cached = getCachedSprite(floorSprite, zoom);
        ctx.drawImage(cached, offsetX + c * tileW, offsetY + r * tileH);
      } else {
        // Fallback to solid color
        const color = getTileColor(tileType, tileColor, c, r);
        if (color) {
          ctx.fillStyle = color;
          ctx.fillRect(offsetX + c * tileW, offsetY + r * tileH, tileW, tileH);
        }
      }
    }
  }

  // ===== WALL BACKGROUND (behind the map) =====
  const wallHeight = 2 * tileH;
  ctx.fillStyle = "#1a1a2e";
  ctx.fillRect(offsetX - wallHeight, offsetY - wallHeight, mapW + 2 * wallHeight, wallHeight);

  // ===== Z-SORTED DRAWABLES (walls + furniture + characters) =====
  const drawables: ZDrawable[] = [];

  // ===== WALL SPRITES (z-sorted 3D walls) =====
  if (hasWallSprites()) {
    const wallInstances = getWallInstances(tileMap, layout.tileColors, layoutCols);
    for (const wall of wallInstances) {
      const wallX = offsetX + wall.x * zoom;
      const wallY = offsetY + wall.y * zoom;
      const cachedWall = getCachedSprite(wall.sprite, zoom);
      drawables.push({
        zY: wall.zY,
        draw: (c) => c.drawImage(cachedWall, wallX, wallY),
      });
    }
  }

  // ===== FURNITURE =====
  const furniturePlacements = getFurniturePlacements(layout);
  for (const placement of furniturePlacements) {
    const asset = placement.asset;
    const img = getCachedImage(placement.type);

    if (!asset) continue;

    // Calculate position - furniture is positioned by its bottom-left tile
    const furnitureX = offsetX + placement.col * tileW;
    // Adjust Y based on backgroundTiles (furniture sprite extends above its footprint)
    const bgTiles = asset.backgroundTiles || 0;
    const furnitureY = offsetY + (placement.row - bgTiles) * tileH;

    // Z-sort by the bottom row of the furniture footprint
    const furnitureZY = (placement.row + asset.footprintH) * TILE_SIZE;

    if (img) {
      // Draw actual furniture image
      const scaledW = asset.width * zoom;
      const scaledH = asset.height * zoom;
      drawables.push({
        zY: furnitureZY,
        draw: (c) => {
          c.drawImage(img, furnitureX, furnitureY, scaledW, scaledH);
        },
      });
    } else {
      // Draw placeholder rectangle for furniture without loaded images
      const scaledW = asset.footprintW * tileW;
      const scaledH = asset.footprintH * tileH;
      const placeholderY = offsetY + placement.row * tileH;
      drawables.push({
        zY: furnitureZY,
        draw: (c) => {
          c.save();
          c.fillStyle = "rgba(139, 105, 20, 0.5)"; // Brown placeholder
          c.fillRect(furnitureX, placeholderY, scaledW, scaledH);
          c.restore();
        },
      });
    }
  }

  // ===== INTERACTIVE OFFICE SUPPLIES =====
  // Render clickable office objects for missing integrations with glow effect
  if (interactiveObjects && interactiveObjects.length > 0) {
    const pulseTime = Date.now() / 1000;

    for (const obj of interactiveObjects) {
      const sprite = getInteractiveSupplySprite(obj.type);
      if (!sprite) continue;

      const objX = offsetX + obj.col * tileW;
      const objY = offsetY + obj.row * tileH;
      const objZY = (obj.row + 1) * TILE_SIZE; // Z-sort by bottom of object

      const isHovered = hoveredObjectId === obj.id;
      const needsAttention = !obj.configured;

      // Calculate pulse alpha for unconfigured objects
      const pulse = Math.sin(pulseTime * INTERACTIVE_GLOW_PULSE_SPEED);
      const glowAlpha = needsAttention
        ? INTERACTIVE_GLOW_MIN_ALPHA + (INTERACTIVE_GLOW_MAX_ALPHA - INTERACTIVE_GLOW_MIN_ALPHA) * ((pulse + 1) / 2)
        : 0;

      const scale = isHovered ? INTERACTIVE_HOVER_SCALE : 1;
      const cached = getCachedSprite(sprite, zoom * scale);

      drawables.push({
        zY: objZY,
        draw: (c) => {
          c.save();

          // Draw glow effect for unconfigured items - subtle indicator
          if (needsAttention && glowAlpha > 0) {
            const glowSize = 4 * zoom; // Subtle glow
            const centerX = objX + (obj.width * zoom) / 2;
            const centerY = objY + (obj.height * zoom) / 2;

            // Create radial gradient glow - brighter
            const gradient = c.createRadialGradient(
              centerX,
              centerY,
              0,
              centerX,
              centerY,
              (obj.width * zoom) / 2 + glowSize,
            );
            gradient.addColorStop(0, `rgba(255, 208, 0, ${glowAlpha})`);
            gradient.addColorStop(0.4, `rgba(255, 208, 0, ${glowAlpha * 0.7})`);
            gradient.addColorStop(0.7, `rgba(255, 208, 0, ${glowAlpha * 0.3})`);
            gradient.addColorStop(1, "rgba(255, 208, 0, 0)");

            c.fillStyle = gradient;
            c.beginPath();
            c.arc(centerX, centerY, (obj.width * zoom) / 2 + glowSize, 0, Math.PI * 2);
            c.fill();
          }

          // Draw the sprite (centered if scaled)
          if (scale !== 1) {
            const offsetForScale = ((scale - 1) * obj.width * zoom) / 2;
            c.drawImage(cached, objX - offsetForScale, objY - offsetForScale);
          } else {
            c.drawImage(cached, objX, objY);
          }

          // Draw hover highlight and tooltip
          if (isHovered && needsAttention) {
            c.strokeStyle = "rgba(255, 255, 255, 0.5)";
            c.lineWidth = 2;
            c.strokeRect(objX - 2, objY - 2, obj.width * zoom + 4, obj.height * zoom + 4);

            // Draw tooltip above the object
            const tooltipText = obj.tooltip;
            const fontSize = Math.max(8, 10 * zoom / 3);
            c.font = `${fontSize}px sans-serif`;
            const textWidth = c.measureText(tooltipText).width;
            const padding = 4 * zoom;
            const tooltipWidth = textWidth + padding * 2;
            const tooltipHeight = fontSize + padding * 1.5;
            const tooltipX = objX + (obj.width * zoom) / 2 - tooltipWidth / 2;
            const tooltipY = objY - tooltipHeight - 4 * zoom;

            // Tooltip background
            c.fillStyle = "rgba(0, 0, 0, 0.85)";
            c.beginPath();
            const radius = 3 * zoom;
            c.roundRect(tooltipX, tooltipY, tooltipWidth, tooltipHeight, radius);
            c.fill();

            // Tooltip text
            c.fillStyle = "#FFFFFF";
            c.textAlign = "center";
            c.textBaseline = "middle";
            c.fillText(tooltipText, objX + (obj.width * zoom) / 2, tooltipY + tooltipHeight / 2);
          }

          c.restore();
        },
      });
    }
  }

  // ===== COLLABORATION/MEETING RELATIONSHIP LINES =====
  // Track which collaboration pairs we've already drawn (avoid duplicates)
  const drawnCollabPairs = new Set<string>();
  const drawnMeetingPairs = new Set<string>();

  // Build a map of character IDs to characters for quick lookup
  const characterMap = new Map<string, Character>();
  for (const ch of characters) {
    characterMap.set(ch.id, ch);
  }

  // Draw collaboration lines (between paired agents)
  for (const ch of characters) {
    if (ch.collaboratingWith && !ch.matrixEffect) {
      const partnerId = ch.collaboratingWith;
      const partner = characterMap.get(partnerId);
      if (partner && !partner.matrixEffect) {
        // Create a consistent pair key to avoid drawing twice
        const pairKey = [ch.id, partnerId].sort().join(":");
        if (!drawnCollabPairs.has(pairKey)) {
          drawnCollabPairs.add(pairKey);

          // Calculate center positions of both characters
          const x1 = offsetX + ch.x * zoom;
          const y1 = offsetY + ch.y * zoom - CONST_TILE_SIZE * zoom * 0.5;
          const x2 = offsetX + partner.x * zoom;
          const y2 = offsetY + partner.y * zoom - CONST_TILE_SIZE * zoom * 0.5;

          // Calculate pulse based on time for subtle animation
          const pulseTime = Date.now() / 1000;
          const pulse = 0.7 + 0.3 * Math.sin(pulseTime * 2);

          // Use minimum zY of both characters minus offset so line appears behind them
          const lineZY = Math.min(ch.y, partner.y) - 1;

          drawables.push({
            zY: lineZY,
            draw: (c) => {
              c.save();

              // Draw glow effect first (wider, more transparent)
              c.strokeStyle = COLLAB_LINE_GLOW_COLOR;
              c.lineWidth = COLLAB_GLOW_WIDTH * zoom * pulse;
              c.lineCap = "round";
              c.setLineDash([]);
              c.beginPath();
              c.moveTo(x1, y1);
              c.lineTo(x2, y2);
              c.stroke();

              // Draw main dotted line
              c.strokeStyle = COLLAB_LINE_COLOR;
              c.lineWidth = COLLAB_LINE_WIDTH * zoom;
              c.setLineDash(COLLAB_LINE_DASH.map((d) => d * zoom));
              c.globalAlpha = 0.6 + 0.4 * pulse;
              c.beginPath();
              c.moveTo(x1, y1);
              c.lineTo(x2, y2);
              c.stroke();

              c.restore();
            },
          });
        }
      }
    }
  }

  // Draw meeting relationship lines (faint connections between all participants)
  for (const ch of characters) {
    if (ch.meetingParticipants && ch.meetingParticipants.length > 0 && !ch.matrixEffect) {
      for (const participantId of ch.meetingParticipants) {
        if (participantId === ch.id) continue; // Skip self
        const participant = characterMap.get(participantId);
        if (participant && !participant.matrixEffect) {
          // Create a consistent pair key to avoid drawing twice
          const pairKey = [ch.id, participantId].sort().join(":");
          if (!drawnMeetingPairs.has(pairKey)) {
            drawnMeetingPairs.add(pairKey);

            // Calculate center positions
            const x1 = offsetX + ch.x * zoom;
            const y1 = offsetY + ch.y * zoom - CONST_TILE_SIZE * zoom * 0.5;
            const x2 = offsetX + participant.x * zoom;
            const y2 = offsetY + participant.y * zoom - CONST_TILE_SIZE * zoom * 0.5;

            // Use minimum zY of both characters minus offset
            const lineZY = Math.min(ch.y, participant.y) - 1;

            drawables.push({
              zY: lineZY,
              draw: (c) => {
                c.save();
                c.strokeStyle = MEETING_LINE_COLOR;
                c.lineWidth = MEETING_LINE_WIDTH * zoom;
                c.lineCap = "round";
                c.setLineDash(MEETING_LINE_DASH.map((d) => d * zoom));
                c.beginPath();
                c.moveTo(x1, y1);
                c.lineTo(x2, y2);
                c.stroke();
                c.restore();
              },
            });
          }
        }
      }
    }
  }

  // Draw "working together" lines (multiple agents on same task)
  const drawnWorkingPairs = new Set<string>();
  for (const ch of characters) {
    if (ch.workingTogetherWith && ch.workingTogetherWith.length > 0 && !ch.matrixEffect) {
      for (const partnerId of ch.workingTogetherWith) {
        const partner = characterMap.get(partnerId);
        if (partner && !partner.matrixEffect) {
          const pairKey = [ch.id, partnerId].sort().join(":");
          if (!drawnWorkingPairs.has(pairKey)) {
            drawnWorkingPairs.add(pairKey);

            const x1 = offsetX + ch.x * zoom;
            const y1 = offsetY + ch.y * zoom - CONST_TILE_SIZE * zoom * 0.5;
            const x2 = offsetX + partner.x * zoom;
            const y2 = offsetY + partner.y * zoom - CONST_TILE_SIZE * zoom * 0.5;

            const pulseTime = Date.now() / 1000;
            const pulse = 0.6 + 0.4 * Math.sin(pulseTime * 3); // Faster pulse for active work

            const lineZY = Math.min(ch.y, partner.y) - 1;

            drawables.push({
              zY: lineZY,
              draw: (c) => {
                c.save();
                // Draw glow
                c.strokeStyle = WORKING_TOGETHER_GLOW_COLOR;
                c.lineWidth = WORKING_TOGETHER_GLOW_WIDTH * zoom * pulse;
                c.lineCap = "round";
                c.setLineDash([]);
                c.beginPath();
                c.moveTo(x1, y1);
                c.lineTo(x2, y2);
                c.stroke();

                // Draw main line
                c.strokeStyle = WORKING_TOGETHER_LINE_COLOR;
                c.lineWidth = WORKING_TOGETHER_LINE_WIDTH * zoom;
                c.globalAlpha = 0.5 + 0.5 * pulse;
                c.beginPath();
                c.moveTo(x1, y1);
                c.lineTo(x2, y2);
                c.stroke();
                c.restore();
              },
            });
          }
        }
      }
    }
  }

  // Draw handoff animations (orb traveling from sender to receiver)
  for (const ch of characters) {
    if (
      ch.handoffProgress !== undefined &&
      ch.handoffProgress !== null &&
      ch.handoffTargetId &&
      !ch.matrixEffect
    ) {
      const target = characterMap.get(ch.handoffTargetId);
      if (target && !target.matrixEffect) {
        const x1 = offsetX + ch.x * zoom;
        const y1 = offsetY + ch.y * zoom - CONST_TILE_SIZE * zoom * 0.5;
        const x2 = offsetX + target.x * zoom;
        const y2 = offsetY + target.y * zoom - CONST_TILE_SIZE * zoom * 0.5;

        const progress = ch.handoffProgress;
        const orbX = x1 + (x2 - x1) * progress;
        const orbY = y1 + (y2 - y1) * progress;

        const lineZY = Math.min(ch.y, target.y) - 0.5;

        drawables.push({
          zY: lineZY,
          draw: (c) => {
            c.save();

            // Draw trail behind orb
            const gradient = c.createLinearGradient(x1, y1, orbX, orbY);
            gradient.addColorStop(0, "rgba(255, 208, 0, 0)");
            gradient.addColorStop(1, HANDOFF_TRAIL_COLOR);
            c.strokeStyle = gradient;
            c.lineWidth = 2 * zoom;
            c.lineCap = "round";
            c.beginPath();
            c.moveTo(x1, y1);
            c.lineTo(orbX, orbY);
            c.stroke();

            // Draw orb
            c.fillStyle = HANDOFF_ORB_COLOR;
            c.beginPath();
            c.arc(orbX, orbY, HANDOFF_ORB_SIZE * zoom, 0, Math.PI * 2);
            c.fill();

            // Orb glow
            c.fillStyle = "rgba(255, 255, 200, 0.5)";
            c.beginPath();
            c.arc(orbX, orbY, (HANDOFF_ORB_SIZE * 0.5) * zoom, 0, Math.PI * 2);
            c.fill();

            c.restore();
          },
        });
      }
    }
  }

  // Draw user focus lines (when user avatar is focused on an agent)
  for (const ch of characters) {
    if (ch.isUserAvatar && ch.focusTargetId && !ch.matrixEffect) {
      const target = characterMap.get(ch.focusTargetId);
      if (target && !target.matrixEffect) {
        const x1 = offsetX + ch.x * zoom;
        const y1 = offsetY + ch.y * zoom - CONST_TILE_SIZE * zoom * 0.5;
        const x2 = offsetX + target.x * zoom;
        const y2 = offsetY + target.y * zoom - CONST_TILE_SIZE * zoom * 0.5;

        const lineZY = Math.min(ch.y, target.y) - 1;

        drawables.push({
          zY: lineZY,
          draw: (c) => {
            c.save();
            c.strokeStyle = USER_FOCUS_LINE_COLOR;
            c.lineWidth = USER_FOCUS_LINE_WIDTH * zoom;
            c.lineCap = "round";
            c.setLineDash(USER_FOCUS_DASH.map((d) => d * zoom));
            c.beginPath();
            c.moveTo(x1, y1);
            c.lineTo(x2, y2);
            c.stroke();
            c.restore();
          },
        });
      }
    }
  }

  for (const ch of characters) {
    const sprites = getCharacterSprites(ch.palette);
    if (!sprites) continue;

    const spriteData = getCharacterSprite(ch, sprites);
    if (!spriteData || spriteData.length === 0) continue;

    const cached = getCachedSprite(spriteData, zoom);
    // Apply different sitting offsets based on state
    const sittingOffset =
      ch.state === CharacterState.SIT_COUCH
        ? CHARACTER_COUCH_SITTING_OFFSET_PX
        : ch.state === CharacterState.TYPE
          ? CHARACTER_SITTING_OFFSET_PX
          : 0;
    const drawX = Math.round(offsetX + ch.x * zoom - cached.width / 2);
    const drawY = Math.round(offsetY + (ch.y + sittingOffset) * zoom - cached.height);
    const charZY = ch.y + TILE_SIZE / 2 + CHARACTER_Z_SORT_OFFSET;

    // Selection/hover outline
    if ((ch.selected || ch.hovered) && !ch.matrixEffect) {
      const outlineAlpha = ch.selected ? SELECTED_OUTLINE_ALPHA : HOVERED_OUTLINE_ALPHA;
      const outlineData = getOutlineSprite(spriteData);
      const outlineCached = getCachedSprite(outlineData, zoom);
      const olDrawX = drawX - zoom;
      const olDrawY = drawY - zoom;

      drawables.push({
        zY: charZY - OUTLINE_Z_SORT_OFFSET,
        draw: (c) => {
          c.save();
          c.globalAlpha = outlineAlpha;
          c.drawImage(outlineCached, olDrawX, olDrawY);
          c.restore();
        },
      });
    }

    // Character sprite (or matrix effect)
    if (ch.matrixEffect) {
      drawables.push({
        zY: charZY,
        draw: (c) => renderMatrixEffect(c, ch, spriteData, drawX, drawY, zoom),
      });
    } else {
      const isSleeping = ch.state === CharacterState.SLEEP;
      const isSittingOnCouch = ch.state === CharacterState.SIT_COUCH;
      drawables.push({
        zY: charZY,
        draw: (c) => {
          c.save();
          if (isSleeping) {
            c.globalAlpha = 0.5;
          }
          if (isSittingOnCouch) {
            // Clip the bottom portion of the sprite to hide legs when sitting
            // This creates the illusion of the character sitting in the chair
            const clipHeight = Math.round(cached.height * 0.65); // Show only upper ~65% of sprite
            c.beginPath();
            c.rect(drawX, drawY, cached.width, clipHeight);
            c.clip();
          }
          c.drawImage(cached, drawX, drawY);
          c.restore();
        },
      });
    }

    // Speech bubble
    const showSleepBubble = (ch.state === CharacterState.SLEEP || ch.bubbleType === "sleep") && !ch.matrixEffect;
    if ((ch.bubbleType || showSleepBubble) && !ch.matrixEffect) {
      const bubbleSprite = showSleepBubble
        ? BUBBLE_SLEEP_SPRITE
        : ch.bubbleType === "permission"
          ? BUBBLE_PERMISSION_SPRITE
          : ch.bubbleType === "message"
            ? BUBBLE_MESSAGE_SPRITE
            : ch.bubbleType === "warning"
              ? BUBBLE_WARNING_SPRITE
              : ch.bubbleType === "setup"
                ? BUBBLE_SETUP_SPRITE
                : ch.bubbleType === "speech"
                  ? BUBBLE_SPEECH_SPRITE
                  : ch.bubbleType === "discuss"
                    ? BUBBLE_DISCUSS_SPRITE
                    : ch.bubbleType === "success"
                      ? BUBBLE_SUCCESS_SPRITE
                      : BUBBLE_WAITING_SPRITE;

      const bubbleCached = getCachedSprite(bubbleSprite, zoom);
      const bubbleOffset =
        ch.state === CharacterState.TYPE || ch.state === CharacterState.SIT_COUCH
          ? BUBBLE_SITTING_OFFSET_PX
          : 0;
      const bubbleX = Math.round(drawX + cached.width / 2 - bubbleCached.width / 2);
      const bubbleY = Math.round(drawY - BUBBLE_VERTICAL_OFFSET_PX * zoom - bubbleOffset * zoom);

      drawables.push({
        zY: charZY + 0.03,
        draw: (c) => c.drawImage(bubbleCached, bubbleX, bubbleY),
      });

      // Draw attention count badge on warning bubbles
      if (ch.bubbleType === "warning" && ch.attentionCount && ch.attentionCount > 0) {
        const badgeX = bubbleX + bubbleCached.width - 4 * zoom;
        const badgeY = bubbleY - 2 * zoom;
        const badgeSize = 8 * zoom;
        drawables.push({
          zY: charZY + 0.04,
          draw: (c) => {
            c.save();
            // Red circle badge
            c.fillStyle = "#CC0000";
            c.beginPath();
            c.arc(badgeX, badgeY, badgeSize / 2, 0, Math.PI * 2);
            c.fill();
            // Count text
            c.fillStyle = "#FFFFFF";
            c.font = `bold ${Math.max(6, (7 * zoom) / 3)}px sans-serif`;
            c.textAlign = "center";
            c.textBaseline = "middle";
            c.fillText(String(ch.attentionCount), badgeX, badgeY);
            c.restore();
          },
        });
      }
    }

    // Name label
    if (ch.name && !ch.matrixEffect) {
      const labelY = drawY - 4 * zoom;
      const centerX = drawX + cached.width / 2;
      drawables.push({
        zY: charZY + 0.01,
        draw: (c) => {
          c.save();
          c.font = `${Math.max(8, (10 * zoom) / 3)}px sans-serif`;
          c.fillStyle = "#ffffff";
          c.textAlign = "center";
          c.fillText(ch.name, centerX, labelY);
          c.restore();
        },
      });
    }

    // Activity/thinking indicator - shows what agent is doing
    const showStatus = ch.isActive && (ch.statusText || ch.currentTool) && !ch.matrixEffect;
    if (showStatus) {
      const statusText = ch.statusText || getToolStatusText(ch.currentTool!);
      const indicatorY = drawY - 14 * zoom;
      const centerX = drawX + cached.width / 2;
      const fontSize = Math.max(7, (9 * zoom) / 3);
      const padding = 3 * zoom;

      drawables.push({
        zY: charZY + 0.02,
        draw: (c) => {
          c.save();
          c.font = `${fontSize}px sans-serif`;

          // Measure text for background
          const textWidth = c.measureText(statusText).width;
          const bgX = centerX - textWidth / 2 - padding;
          const bgY = indicatorY - fontSize / 2 - padding / 2;
          const bgW = textWidth + padding * 2;
          const bgH = fontSize + padding;

          // Semi-transparent dark background
          c.fillStyle = "rgba(0, 0, 0, 0.7)";
          c.beginPath();
          c.roundRect(bgX, bgY, bgW, bgH, 3 * zoom);
          c.fill();

          // Status text in cyan/green
          c.fillStyle = "#66ffcc";
          c.textAlign = "center";
          c.textBaseline = "middle";
          c.fillText(statusText, centerX, indicatorY);
          c.restore();
        },
      });
    }

    // Metrics overlay (above activity indicator for active agents)
    if (ch.isActive && ch.metricsOverlay && !ch.matrixEffect) {
      const metrics = ch.metricsOverlay;
      const hasMetrics =
        metrics.tokensPerSec !== undefined ||
        metrics.costCents !== undefined ||
        metrics.latencyMs !== undefined;

      if (hasMetrics) {
        const metricsY = drawY - 20 * zoom;
        const centerX = drawX + cached.width / 2;

        // Calculate staleness (fade if > 5s old)
        const now = Date.now();
        const age = metrics.lastUpdated ? now - metrics.lastUpdated : 0;
        const isStale = age > 5000;
        const baseAlpha = isStale ? 0.4 + 0.2 * Math.sin(now / 300) : 0.9;

        // Build metrics text parts
        const parts: string[] = [];
        if (metrics.tokensPerSec !== undefined && metrics.tokensPerSec > 0) {
          parts.push(`${Math.round(metrics.tokensPerSec)}t/s`);
        }
        if (metrics.costCents !== undefined) {
          parts.push(`$${(metrics.costCents / 100).toFixed(2)}`);
        }
        if (metrics.latencyMs !== undefined) {
          parts.push(`${Math.round(metrics.latencyMs)}ms`);
        }

        if (parts.length > 0) {
          const metricsText = parts.join(" | ");
          const fontSize = Math.max(5, (7 * zoom) / 3);

          // Cost color coding: green < $0.01, yellow < $0.10, red >= $0.10
          let costColor = "#88ff88"; // green (low cost)
          if (metrics.costCents !== undefined) {
            if (metrics.costCents >= 10) {
              costColor = "#ff6666"; // red (high cost)
            } else if (metrics.costCents >= 1) {
              costColor = "#ffcc44"; // yellow (medium cost)
            }
          }

          drawables.push({
            zY: charZY + 0.025,
            draw: (c) => {
              c.save();
              c.globalAlpha = baseAlpha;
              c.font = `${fontSize}px monospace`;

              // Measure text for background pill
              const textWidth = c.measureText(metricsText).width;
              const pillPadding = 3 * zoom;
              const pillHeight = fontSize + 2 * zoom;
              const pillWidth = textWidth + pillPadding * 2;
              const pillX = centerX - pillWidth / 2;
              const pillY = metricsY - pillHeight + 1 * zoom;

              // Semi-transparent dark background pill
              c.fillStyle = "rgba(0, 0, 0, 0.6)";
              c.beginPath();
              const radius = 2 * zoom;
              c.roundRect(pillX, pillY, pillWidth, pillHeight, radius);
              c.fill();

              // Metrics text
              c.fillStyle = costColor;
              c.textAlign = "center";
              c.fillText(metricsText, centerX, metricsY);
              c.restore();
            },
          });
        }
      }
    }
  }

  // Sort by Y and draw
  drawables.sort((a, b) => a.zY - b.zY);
  for (const d of drawables) {
    d.draw(ctx);
  }
}
